/**
 * Term normalization + tokenization for ctgSearch.
 *
 * The indexer and the query path MUST produce identical tokens for the
 * same input or fuzzy matching breaks down. Everything in this file is
 * used by both.
 *
 * Normalization is three stages:
 *   1. Case-fold + strip punctuation other than hyphens.
 *   2. Apply the SYNONYMS table (edit this to add marketing-driven
 *      synonyms over time -- "crv" -> "cr-v", etc.).
 *   3. Tokenize on whitespace AND hyphen boundaries, but also keep the
 *      un-split hyphenated form as an additional token so "cr-v" matches
 *      as a single unit.
 */


/**
 * Query/indexer synonyms. Left side is the normalized input, right side
 * is the canonical form it rewrites to. Both sides are run through
 * tokenization again after substitution.
 *
 * NOTE: this is intentionally NOT the Algolia dashboard's synonym list.
 * Rebuild it from marketing input over time; the current entries cover
 * the known cases that SearchInput.vue's switch statement was special-
 * casing before ctgSearch existed.
 */
const SYNONYMS = {
  "crv":  "cr-v",
  "hrv":  "hr-v",
  "cr v": "cr-v",
  "hr v": "hr-v",
};


/**
 * Lowercase, strip punctuation except hyphens, collapse whitespace.
 * Preserves hyphens so model names like "cr-v" don't get split before
 * synonym substitution.
 */
export function normalize(input) {
  if (input == null) return "";
  let s = String(input).toLowerCase();
  // Replace any non [a-z0-9\s-] with a space.
  s = s.replace(/[^a-z0-9\s-]+/g, " ");
  // Collapse whitespace runs.
  s = s.replace(/\s+/g, " ").trim();
  return s;
}


/**
 * Apply the SYNONYMS table. We check both exact-string match of the
 * whole normalized input (fastest, handles "crv") and per-token match
 * so a longer query like "used crv ex" gets "crv" rewritten.
 */
function applySynonyms(normalized) {
  if (SYNONYMS[normalized] !== undefined) return SYNONYMS[normalized];
  let out = normalized;
  for (const [from, to] of Object.entries(SYNONYMS)) {
    // Word-boundary replace of the synonym. The source side is already
    // normalized so no case or punctuation surprises.
    const pattern = new RegExp("(^|\\s)" + from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(\\s|$)", "g");
    out = out.replace(pattern, "$1" + to + "$2");
  }
  return out;
}


/**
 * Produce the set of tokens for a value. Hyphenated terms contribute
 * both the full form and the split parts, so "cr-v" indexes under
 * "cr-v", "cr", and "v" -- the "cr-v" token lets the exact phrase score
 * highest, and the parts let "cr v" (post-synonym) match too.
 *
 * Empty tokens and single-character tokens are dropped (noise).
 */
export function tokenize(value) {
  const norm = applySynonyms(normalize(value));
  if (!norm) return [];
  const seen = new Set();
  const push = (t) => {
    if (t && t.length > 1) seen.add(t);
  };
  // Split on whitespace first, then on hyphens within each piece.
  for (const piece of norm.split(/\s+/)) {
    push(piece);
    if (piece.indexOf("-") >= 0) {
      for (const sub of piece.split("-")) push(sub);
    }
  }
  return Array.from(seen);
}


/**
 * Which payload fields participate in fuzzy text search, and their
 * boost multipliers. Mirrors the current Algolia searchableAttributes
 * priority in search/index.py.
 *
 * Fields not listed here are either filter/sort-only (price, year,
 * mileage, etc.) or exact-only (stock_number, vin, certification).
 */
export const FUZZY_FIELDS = {
  model: 3.0,
  make: 2.0,
  trim: 1.5,
  body: 1.0,
  color: 1.0,
  colour: 1.0,
  features: 1.0,
  feature_list: 1.0,
  drivetrain_description: 1.0,
};


/**
 * Fields where a typo should NEVER match -- a stock number is either
 * exactly right or it's wrong. These are indexed too, but only for
 * exact lookups during query processing.
 */
export const EXACT_FIELDS = ["stock_number", "vin"];
